import React, {useEffect, useRef, useState, useContext} from 'react'
import {
    Box,
    Text,
    useStyleConfig,
    Heading,
    createStylesContext,
    Stack,
    Accordion,
    useBreakpointValue,
    useDisclosure,
    InputRightElement,
    IconButton,
    Skeleton,
    SimpleGrid
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useIntl} from 'react-intl'
import {useForm} from 'react-hook-form'

import colors from '../../theme/foundations/colors'
import {usePage} from '@salesforce/commerce-sdk-react'
import {PageContext, Component} from '@salesforce/commerce-sdk-react/components'
import {
    PDCFooterLinksListMobile,
    PDCFooterLinksListDesktop,
    PDCFooterLinksListCheckout,
    PDCFooterReinsurance
} from '../../page-designer/layouts'
import {PDCFooterReinsuranceIcon, PDCLink} from '../../page-designer/assets'
import FooterBottom from './footer-bottom'
import {useLocation, useHistory} from 'react-router-dom'
import {NewsletterModal} from '../newsletter-modal'
import {NewsletterSubscriptionModal} from '../newsletter-subscription-modal'
import {useSplioService} from './../../hooks/use-splio-service'
import useMultiSite from '../../hooks/use-multi-site'
import Field from '../field'
import {RightArrowIcon, TurtleIcon} from '../custom-icons'
import FieldsetForm from '../shared/ui/FieldsetForm'
import useNewsletterFields from '../forms/useNewsletterFields'
import {useIsLocale} from '../../hooks/use-is-locale'
import {useProfile} from './../../hooks/use-update-profile-attributes.js'
import {IPContext} from '../../custom-contexts'
import {useXSSProtection} from './../../hooks/use-xss-protection.js'

// Utils
import {getEnvStaleTime, getPageDesignerRegion} from '../../utils/utils'
import {useToast} from '../../hooks/use-toast.js'
import PropTypes from 'prop-types'

const PAGEDESIGNER_TO_COMPONENT_MOBILE = {
    'commerce_assets.pdcLink': PDCLink,
    'commerce_layouts.pdcFooterLinksList': PDCFooterLinksListMobile
}

const PAGEDESIGNER_TO_COMPONENT_DESKTOP = {
    'commerce_assets.pdcLink': PDCLink,
    'commerce_layouts.pdcFooterLinksList': PDCFooterLinksListDesktop
}

const PAGEDESIGNER_TO_COMPONENT_CHECKOUT = {
    'commerce_assets.pdcLink': PDCLink,
    'commerce_layouts.pdcFooterLinksList': PDCFooterLinksListCheckout
}
const PAGEDESIGNER_TO_COMPONENT_REINSURANCE = {
    'commerce_assets.pdcFooterReinsuranceIcon': PDCFooterReinsuranceIcon,
    'commerce_layouts.pdcFooterReinsurance': PDCFooterReinsurance
}
const [StylesProvider] = createStylesContext('Footer')

/**
 * Renders the Footer component for the application.
 *
 * The Footer component provides various functionalities including newsletter subscription,
 * displaying navigation links, and handling different layouts for desktop and mobile views.
 * It also manages the subscription process by interacting with external services to check
 * subscription status and blacklist status of email addresses.
 *
 * @param {Object} props - The properties passed to the Footer component.
 * @param {boolean} [props.simplified=false] - Determines if the footer should be rendered in a simplified mode.
 *
 * @returns {JSX.Element} The rendered Footer component.
 *
 * @example
 * // Render the Footer component
 * <Footer simplified={true} />
 *
 * @description
 * - The component uses various hooks and context providers to manage state and perform actions.
 * - It includes a newsletter subscription form that validates email addresses and checks their subscription status.
 * - Depending on the user's location and subscription status, it either opens a modal or forwards account details to an external service.
 * - The component adapts its layout based on the screen size, providing different views for desktop and mobile.
 */
const Footer = ({simplified = false}) => {
    const {updateProfileConsentAttributes} = useProfile()
    const [newsletterEmail, setNewsletterEmail] = useState(null)
    const [previousNewsletterEmail, setPreviousNewsletterEmail] = useState(null)
    const [emailAlreadySubmitted, setEmailAlreadySubmitted] = useState(false)
    const styles = useStyleConfig('Footer')
    const showToast = useToast()
    const {formatMessage} = useIntl()
    const isDesktop = useBreakpointValue(
        {base: false, lg: true},
        {
            fallback: undefined
        }
    )
    const location = useLocation()
    const {isAlreadySubscribed, isBlackListedEmail, forwardAccountToSplio} = useSplioService()
    const {site, locale, buildUrl} = useMultiSite()
    const isCheckout = /\/checkout$/.test(location?.pathname)
    const isCheckoutOrConfirm = /\/checkout/.test(location?.pathname)
    const {isIPBlocked} = useContext(IPContext)
    const [newsletterSubmitDisabled, setNewsletterSubmitDisabled] = useState(false)
    const history = useHistory()
    const {validateXssForm} = useXSSProtection()

    // Get the header page from page designer to display the navigation links
    const staleTime = getEnvStaleTime()
    const {data: footerPage} = usePage({parameters: {pageId: 'footer'}}, {staleTime: staleTime})
    const footerLinksListComponents = getPageDesignerRegion(footerPage, 'main').components?.filter(
        (comp) => comp?.typeId === 'commerce_layouts.pdcFooterLinksList'
    )
    const footerLinksListCheckout = footerLinksListComponents?.filter(
        (footerLinksList) => footerLinksList.data.checkoutLinks === true
    )
    const footerLinksListNotCheckout = footerLinksListComponents?.filter(
        (footerLinksList) => footerLinksList.data.checkoutLinks === false
    )
    const [contextValueMobile] = useState({
        components: PAGEDESIGNER_TO_COMPONENT_MOBILE
    })
    const [contextValueDesktop] = useState({
        components: PAGEDESIGNER_TO_COMPONENT_DESKTOP
    })

    const [contextValueCheckout] = useState({
        components: PAGEDESIGNER_TO_COMPONENT_CHECKOUT
    })
    const newsletterButtonRef = useRef(null)
    const [newsletterButtonWidth, setNewsletterButtonWidth] = useState(0)

    /**
     * A React hook that sets up an IntersectionObserver to monitor the visibility of a newsletter button.
     *
     * This hook observes the newsletter button element and updates its width state when the button becomes visible
     * in the viewport. It uses the IntersectionObserver API to efficiently detect when the button enters the viewport,
     * and once the button is visible, it sets the button's width and disconnects the observer to avoid unnecessary
     * computations.
     *
     * The observer is configured with a threshold of 0.1, meaning the callback is triggered when 10% of the button
     * is visible in the viewport.
     *
     * The hook also includes a cleanup function that disconnects the observer when the component is unmounted or
     * when the dependencies change, preventing memory leaks.
     *
     * Dependencies:
     * - `newsletterButtonRef`: A React ref object pointing to the newsletter button DOM element.
     * - `setNewsletterButtonWidth`: A state setter function to update the width of the newsletter button.
     */
    useEffect(() => {
        const button = newsletterButtonRef.current
        if (!button) return

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setNewsletterButtonWidth(button.offsetWidth)
                        observer.disconnect()
                    }
                })
            },
            {threshold: 0.1}
        )

        observer.observe(button)

        // Cleanup function
        return () => {
            observer.disconnect()
        }
    }, [])

    const checkoutFooterStyle = isDesktop
        ? {
              display: 'flex',
              flexDirection: 'row-reverse',
              justifyContent: 'space-between',
              pt: '70px'
          }
        : null
    const form = useForm()
    const {
        formState: {isSubmitting}
    } = form
    const fields = useNewsletterFields({form, prefix: 'newsletter_footer'})

    const {
        isOpen: isNewsletterModalOpen,
        onOpen: onNewsletterModalOpen,
        onClose: onNewsletterModalClose
    } = useDisclosure()

    /**
     * Handles the closing of the newsletter modal.
     *
     * This function resets the newsletter email state to null and then
     * calls the `onNewsletterModalClose` function to close the modal.
     */
    const handleNewsletterModalClose = () => {
        setPreviousNewsletterEmail(newsletterEmail)
        setNewsletterEmail(null)
        setNewsletterSubmitDisabled(false)
        onNewsletterModalClose()
    }

    /**
     * Handles the newsletter subscription process based on the provided data.
     *
     * This function checks if the email is present in the data object. If an email
     * is provided, it sets the newsletter email state and determines the appropriate
     * action based on the user's location (US or non-US).
     *
     * @async
     * @function handleNewsletterSubscription
     * @param {Object} data - The data object containing subscription details.
     * @param {string} data.newsletter_footeremail - The email address to be subscribed.
     *
     * @returns {void}
     *
     * @example
     * handleNewsletterSubscription({ newsletter_footeremail: 'user@example.com' });
     *
     * @description
     * - If the email is not present in the data object, the function exits early.
     * - If the user is located in the US, it triggers the Splio call with the email.
     * - If the user is not located in the US, it opens the newsletter modal.
     */
    const isUS = useIsLocale('en-US')

    const handleNewsletterSubscription = async (data) => {
        if (isIPBlocked) {
            const isRootPath = location.pathname === '/'
            if (isRootPath) {
                return
            } else {
                const path = buildUrl('/')
                history.push(path)
                return
            }
        }

        if (!data.newsletter_footeremail) {
            return
        }

        const isXssDetected = validateXssForm(data)
        if (isXssDetected) {
            return
        }

        setNewsletterEmail(data.newsletter_footeremail)
    }
    /**
     * Checks if the email in the request object is already subscribed.
     *
     * This function calls the `isAlreadySubscribed` service to determine if the email
     * provided in the `requestObject` is already subscribed to the newsletter or service.
     *
     * @param {Object} requestObject - The request object containing the email and other relevant data.
     * @param {string} requestObject.email - The email address to check for subscription status.
     * @returns {Promise<Object>} A promise that resolves to the result of the subscription check.
     */
    const emailAlreadySubscribed = async (requestObject) => await isAlreadySubscribed(requestObject)

    /**
     * Checks if the email in the request object is blacklisted.
     *
     * This function calls the `isBlackListedEmail` service to determine if the email
     * provided in the `requestObject` is blacklisted.
     *
     * @param {Object} requestObject - The request object containing the email and other relevant data.
     * @param {string} requestObject.email - The email address to check against the blacklist.
     * @returns {Promise<Object>} A promise that resolves to the result of the blacklist check.
     */
    const emailBlackListed = async (requestObject) => await isBlackListedEmail(requestObject)

    /**
     * Asynchronously checks the subscription status of a given email for a newsletter
     * and handles the response based on the subscription status.
     *
     * This function performs several checks on the provided email:
     * 1. Verifies if the email is already subscribed to the newsletter.
     * 2. Checks if the email is blacklisted.
     * 3. Depending on the user's location (US or not), it either handles a specific
     *    call to Splio or opens a newsletter modal.
     *
     * @param {string} newsletterEmail - The email address to check for newsletter subscription status.
     *
     * @returns {Promise<void>} - A promise that resolves when the subscription status check and
     *                            subsequent actions are complete.
     *
     * @throws {Error} - Throws an error if any of the asynchronous operations fail.
     *
     * @example
     * // Check the subscription status of an email
     * newsletterEmailStatus('example@example.com');
     */
    const newsletterEmailStatus = async (newsletterEmail) => {
        if (emailAlreadySubmitted) {
            showToast({
                title: formatMessage({
                    defaultMessage: 'This email is already subscribed',
                    id: 'newsletter_form.error.email_already_subscribed'
                }),
                status: 'error',
                description: ''
            })
            setNewsletterSubmitDisabled(true)
            return
        }
        if (!newsletterEmail) {
            return
        }

        const requestObject = {
            email: newsletterEmail,
            siteId: site.id,
            locale: locale.id,
            requestType: 'footer-newsletter-subscription'
        }

        const alreadySubscribed = await emailAlreadySubscribed(requestObject)
        setNewsletterSubmitDisabled(true)
        if (alreadySubscribed.success) {
            showToast({
                title: alreadySubscribed.message,
                status: 'error',
                description: ''
            })
        } else {
            const blackListedEmail = await emailBlackListed(requestObject)
            if (blackListedEmail.success) {
                showToast({
                    title: blackListedEmail.message,
                    status: 'error',
                    description: ''
                })
            } else {
                if (isUS) {
                    await handleSplioCall()
                } else {
                    onNewsletterModalOpen()
                }
            }
        }
    }

    useEffect(() => {
        newsletterEmailStatus(newsletterEmail)
    }, [newsletterEmail])
    /**
     * Handles the subscription process for a newsletter.
     *
     * This asynchronous function manages the subscription process by first checking if the provided
     * email is already subscribed to the newsletter. If the email is already subscribed, it sets an
     * error on the form indicating the subscription status. If not, it further checks if the email
     * is blacklisted. If the email is blacklisted, it sets an appropriate error on the form. If the
     * email passes both checks, it forwards the account details to Splio and displays a success
     * message to the user.
     *
     * @async
     * @function handleSplioCall
     * @returns {Promise<void>} - A promise that resolves when the subscription process is complete.
     */
    const handleSplioCall = async () => {
        if (!newsletterEmail) {
            return
        }
        await forwardAccountToSplio({
            email: newsletterEmail,
            firstName: '',
            lastName: '',
            phoneHome: '',
            marketingConsent: true,
            newsletterSection: 'footer',
            requestType: 'footer-newsletter-subscription'
        })

        await updateProfileConsentAttributes(newsletterEmail)

        showToast({
            title: formatMessage({
                defaultMessage: 'Subscription successful',
                id: 'newsletter.subscription_successful'
            }),
            status: 'success',
            description: ''
        })
        setPreviousNewsletterEmail(newsletterEmail)
        setNewsletterEmail(null)
        form.reset()
    }

    const newsletterSubmit = form.handleSubmit(handleNewsletterSubscription)

    return (
        <Box as="footer" id="footer-main" {...styles.container}>
            {isCheckout ? null : <PreFooter></PreFooter>}
            <Box {...styles.content} bg={colors.lightBeige}>
                <StylesProvider value={styles}>
                    <Box sx={isCheckout ? checkoutFooterStyle : null}>
                        <Stack
                            direction={{base: 'column', lg: 'row'}}
                            spacing={'44px'}
                            pt={simplified ? {base: 0, lg: '44px'} : '44px'}
                            pb={
                                simplified || isCheckout
                                    ? {base: 0, lg: '44px'}
                                    : {base: '44px', lg: '140px'}
                            }
                            px={{base: '10px', lg: '20px'}}
                        >
                            {isCheckout ? null : simplified ? (
                                <Box flexBasis={'33%'}></Box>
                            ) : (
                                <Box flexBasis={'33%'}>
                                    <Heading
                                        as="h2"
                                        fontSize="3xl"
                                        textTransform={'uppercase'}
                                        mb={13}
                                    >
                                        {formatMessage({
                                            id: 'footer.newsletter.title',
                                            defaultMessage: 'Newsletter'
                                        })}
                                    </Heading>
                                    <Text variant="bodyBase2" color={colors.darkGray} maxW={'280'}>
                                        {formatMessage({
                                            id: 'footer.newsletter.info',
                                            defaultMessage:
                                                'Sign up to get the latest on our new arrivals, products and events.'
                                        })}
                                    </Text>
                                    <FieldsetForm
                                        id="newsletter-form"
                                        legendText={formatMessage({
                                            defaultMessage: 'Newsletter form',
                                            id: 'newsletter_form.form.legend'
                                        })}
                                        onSubmit={(e) => {
                                            newsletterSubmit(e)
                                        }}
                                        error={form.formState.errors?.global?.message}
                                        isMandatoryNote={false}
                                    >
                                        <Box maxW={'343px'} mt={'34px'}>
                                            <Field
                                                {...fields.email}
                                                inputProps={{
                                                    sx: {
                                                        background: colors.lightBeige,
                                                        paddingRight: newsletterButtonWidth
                                                    }
                                                }}
                                                customProps={{
                                                    onChange: (value) => {
                                                        setEmailAlreadySubmitted(
                                                            value === previousNewsletterEmail
                                                        )
                                                        setNewsletterSubmitDisabled(false)
                                                    }
                                                }}
                                            >
                                                <InputRightElement ref={newsletterButtonRef}>
                                                    <IconButton
                                                        type="submit"
                                                        aria-label={formatMessage({
                                                            defaultMessage: 'Submit newsletter',
                                                            id: 'newsletter_form.form.submit'
                                                        })}
                                                        size="inputElement"
                                                        icon={
                                                            <RightArrowIcon
                                                                color="fullBlack"
                                                                boxSize={5}
                                                            />
                                                        }
                                                        isLoading={isSubmitting}
                                                        isDisabled={newsletterSubmitDisabled}
                                                    />
                                                </InputRightElement>
                                            </Field>
                                        </Box>
                                    </FieldsetForm>
                                    <NewsletterModal
                                        isOpen={isNewsletterModalOpen}
                                        onClose={handleNewsletterModalClose}
                                        submitNewsletter={() => {
                                            handleSplioCall()
                                            onNewsletterModalClose()
                                        }}
                                    ></NewsletterModal>
                                    {!isCheckoutOrConfirm && <NewsletterSubscriptionModal />}
                                </Box>
                            )}
                            <Box flexBasis={isCheckout ? null : '33%'} alignSelf={'center'}>
                                <TurtleIcon
                                    width={{base: '193px', lg: '234px'}}
                                    height={{base: '190px', lg: '230px'}}
                                    display={isCheckout ? {base: 'none', lg: 'block'} : 'block'}
                                    margin={'auto'}
                                    color={'#112A46'}
                                />
                            </Box>
                            {isCheckout ? null : (
                                <Box flexBasis={'33%'} textAlign={{base: 'left', lg: 'right'}}>
                                    {/* TODO: TO REMOVE ONCE REAL STARS RATING IS IMPLEMENTED */}
                                </Box>
                            )}
                        </Stack>
                        <Box px={{base: isCheckout ? '10px' : '0', lg: '20px'}} flexGrow={1}>
                            {isCheckout ? (
                                <PageContext.Provider value={contextValueCheckout}>
                                    <Stack direction={'row'}>
                                        {footerLinksListCheckout?.map((footerLinksList, index) => (
                                            <Box
                                                key={index}
                                                flex={'0 1 20%'}
                                                mb={isDesktop ? '' : '44px'}
                                            >
                                                <Component component={footerLinksList} />
                                            </Box>
                                        ))}
                                    </Stack>
                                </PageContext.Provider>
                            ) : isDesktop === undefined ? (
                                !simplified && (
                                    <Stack direction={['column', null, 'row']} gap={0}>
                                        {footerLinksListNotCheckout?.map((_, index) => (
                                            <Stack
                                                key={index}
                                                width={['100%', null, null, '20%']}
                                                gap={[0, null, null, 2]}
                                            >
                                                <Skeleton
                                                    height={['50px', null, null, '14px']}
                                                    mb={[0, null, null, '20px']}
                                                    display={[
                                                        index ===
                                                        footerLinksListNotCheckout?.length - 1
                                                            ? 'none'
                                                            : 'block',
                                                        null,
                                                        null,
                                                        'block'
                                                    ]}
                                                />
                                                {Array.from({length: 7}).map((_, index) => (
                                                    <Skeleton
                                                        key={index}
                                                        display={['none', null, null, 'block']}
                                                        height={'14px'}
                                                        my={5}
                                                    />
                                                ))}
                                                {index ===
                                                    footerLinksListNotCheckout?.length - 1 && (
                                                    <SimpleGrid
                                                        columns={2}
                                                        spacing={'10px'}
                                                        my={'40px'}
                                                        px={'10px'}
                                                        display={['grid', null, null, 'none']}
                                                    >
                                                        {Array.from({length: 7}).map((_, index) => (
                                                            <Skeleton
                                                                key={index}
                                                                height={'14px'}
                                                                my={5}
                                                            />
                                                        ))}
                                                    </SimpleGrid>
                                                )}
                                            </Stack>
                                        ))}
                                    </Stack>
                                )
                            ) : isDesktop ? (
                                !simplified && (
                                    <PageContext.Provider value={contextValueDesktop}>
                                        <Stack direction={'row'}>
                                            {footerLinksListNotCheckout?.map(
                                                (footerLinksList, index) => (
                                                    <Box key={index} flex={'0 1 20%'}>
                                                        <Component component={footerLinksList} />
                                                    </Box>
                                                )
                                            )}
                                        </Stack>
                                    </PageContext.Provider>
                                )
                            ) : (
                                !simplified && (
                                    <PageContext.Provider value={contextValueMobile}>
                                        <Accordion allowToggle mb={'44px'}>
                                            {footerLinksListNotCheckout?.map(
                                                (footerLinksList, index) => (
                                                    <Component
                                                        key={index}
                                                        component={footerLinksList}
                                                    />
                                                )
                                            )}
                                        </Accordion>
                                    </PageContext.Provider>
                                )
                            )}
                        </Box>
                    </Box>
                    <FooterBottom isMinimal={simplified} />
                </StylesProvider>
            </Box>
        </Box>
    )
}

Footer.propTypes = {
    simplified: PropTypes.bool
}

export default Footer

/**
 * PreFooter Component
 *
 * This component renders the pre-footer section, which includes reinsurance information.
 * It dynamically loads the reinsurance component based on the current page context.
 *
 * @component
 * @example
 * // Usage example:
 * <PreFooter />
 *
 * @returns {JSX.Element} The rendered pre-footer component.
 */

const PreFooter = () => {
    const {locale} = useMultiSite()
    const staleTime = getEnvStaleTime()
    const {data: footerPage} = usePage(
        {parameters: {pageId: 'footer', locale: locale?.id}},
        {staleTime: staleTime, keepPreviousData: true}
    )
    const footerReinsuranceComponent = getPageDesignerRegion(footerPage, 'main').components?.find(
        (comp) => comp?.typeId === 'commerce_layouts.pdcFooterReinsurance'
    )
    const [contextValueReinsurance] = useState({
        components: PAGEDESIGNER_TO_COMPONENT_REINSURANCE
    })
    return (
        <>
            {footerReinsuranceComponent && (
                <PageContext.Provider value={contextValueReinsurance}>
                    <Component component={footerReinsuranceComponent} />
                </PageContext.Provider>
            )}
        </>
    )
}
PreFooter.propTypes = {}
